import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Monitor, FlaskConical, BookOpenCheck, Dumbbell, Bus, UtensilsCrossed } from "lucide-react";

const facilities = [
  {
    title: "Smart Classrooms",
    description: "Interactive digital boards and modern learning technology",
    icon: Monitor,
    image: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Science Labs",
    description: "Well-equipped physics, chemistry, and biology laboratories",
    icon: FlaskConical,
    image: "https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Library",
    description: "Extensive collection of books and digital resources",
    icon: BookOpenCheck,
    image: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Sports Ground",
    description: "Large playground with facilities for multiple sports",
    icon: Dumbbell,
    image: "https://images.unsplash.com/photo-1461896836934-eba09b295a37?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Transport",
    description: "Safe and GPS-enabled buses covering all major routes",
    icon: Bus,
    image: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Cafeteria",
    description: "Hygienic food with nutritious meal options",
    icon: UtensilsCrossed,
    image: "https://images.unsplash.com/photo-1567521464027-f127ff144326?auto=format&fit=crop&w=600&q=80",
  },
];

export const FacilitiesSection = () => {
  return (
    <section className="py-16 md:py-24 bg-slate-50" data-testid="facilities-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-secondary font-medium text-sm uppercase tracking-wider">
            Our Facilities
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
            World-Class Infrastructure
          </h2>
          <div className="section-divider mx-auto"></div>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            State-of-the-art facilities designed to provide the best learning 
            environment for our students.
          </p>
        </motion.div>

        {/* Facilities Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {facilities.map((facility, index) => (
            <motion.div
              key={facility.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-300 group"
              data-testid={`facility-${index}`}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={facility.image}
                  alt={facility.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                  <facility.icon className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                  {facility.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {facility.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center"
        >
          <Link to="/facilities">
            <Button className="bg-primary hover:bg-primary/90 rounded-full px-8">
              View All Facilities
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
